// VX L4-10 — KdState: 404 · error · 빈 상태의 «한 가지 판»(순수 · props 만 · 훅 없음 — 서버·클라이언트 어디서나).
//
// 문법(킥커 보드 — 2026-10-12 블라인드 채점 21.0 / 현행 12.0, docs/vx/ledger_L4-10.md):
//   골드 룰 + 킥커(무슨 상태인가) → 제목(무슨 일인가, 한 줄) → 안내 문장(«다음 행동») → 버튼 → (선택) 바로 갈 곳 행.
//
// 카피 규칙(DS_RULES#2-5):
//   ⛔ 사과하지 않는다. ⛔ 「비어 있음」과 「못 불러옴」을 같은 말로 뭉치지 않는다(kind 로 가른다).
//   ✅ hint 는 다음 행동을 말한다. 가능하면 actions 로 그 길을 같이 준다.
// ⛔ 이모지·아이콘 글리프를 그리지 않는다(상태 식별은 킥커 텍스트가 진다 — 색만으로 말하지 않는다).
import React from 'react';
import Link from 'next/link';
import './kd-shell.css';

export type KdStateKind = 'notfound' | 'error' | 'empty';

export interface KdStateAction {
  label: string;
  /** href 가 있으면 링크, 없으면 버튼(onClick). */
  href?: string;
  onClick?: () => void;
  primary?: boolean;
  /** true 면 <a>(전체 새로 불러오기) — 라우터가 깨졌을 수 있는 오류 경계에서 쓴다. */
  hard?: boolean;
}

export interface KdStateLink { label: string; href: string; note?: string }

export interface KdStateProps {
  kind: KdStateKind;
  /** 킥커 — 상태 이름(예: 「404 · 없는 주소」). 빈 상태는 생략할 수 있다. */
  kicker?: string;
  title: string;
  /** 다음 행동 안내 문장. */
  hint?: React.ReactNode;
  actions?: KdStateAction[];
  /** 바로 갈 곳(행) — 404 전용에 가깝다. */
  links?: KdStateLink[];
  linksLabel?: string;
  /** 오류 코드(digest). */
  code?: string | null;
  headingAs?: 'h1' | 'h2' | 'h3' | 'p';
  /** 빈 상태의 보조 칩 링크. */
  suggestions?: KdStateLink[];
  className?: string;
  children?: React.ReactNode;
}

function Action({ a }: { a: KdStateAction }) {
  const cls = a.primary ? 'kd-st-btn kd-st-btn--primary' : 'kd-st-btn';
  if (a.href && a.hard) return <a href={a.href} className={cls}>{a.label}</a>;
  if (a.href) return <Link href={a.href} className={cls}>{a.label}</Link>;
  return <button type="button" className={cls} onClick={a.onClick}>{a.label}</button>;
}

export default function KdState({ kind, kicker, title, hint, actions, links, linksLabel = '바로 갈 곳', code, headingAs, suggestions, className, children }: KdStateProps) {
  const H = headingAs ?? (kind === 'empty' ? 'p' : 'h2');
  const split = !!links && links.length > 0;
  const cls = ['kd-st', `kd-st--${kind}`, split ? 'kd-st--split' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <div className={cls} data-kd="state" data-kd-state={kind} role={kind === 'error' ? 'alert' : undefined}>
      <div className="kd-st-b">
        {kicker ? <p className="kd-st-k">{kicker}</p> : null}
        <H className="kd-st-t">{title}</H>
        {hint ? <p className="kd-st-d">{hint}</p> : null}
        {children}
        {actions && actions.length > 0 ? (
          <div className="kd-st-a">{actions.map((a) => <Action key={a.label} a={a} />)}</div>
        ) : null}
        {suggestions && suggestions.length > 0 ? (
          <ul className="kd-st-sugg">{suggestions.map((s) => <li key={s.href}><Link href={s.href}>{s.label}</Link></li>)}</ul>
        ) : null}
        {code ? <p className="kd-st-code">오류 코드 {code}</p> : null}
      </div>
      {split ? (
        <div className="kd-st-side">
          <p className="kd-st-lh">{linksLabel}</p>
          <ul className="kd-st-l">
            {links!.map((l) => (
              <li key={l.href}><Link href={l.href}><span>{l.label}</span>{l.note ? <span>{l.note}</span> : null}</Link></li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
